import React, { FC, useState } from "react";
import { StrictUnion } from "simplytyped";
import { SingleFieldModal } from "../../../SingleFieldModal";
import { InsuranceFormSubmissionStatus } from "../../../../Utils/types";
import { LoadingAnimation } from "../../../../../LoadingAnimation/LoadingAnimation";
import { InsuranceDoctorNoteDto } from "@apiModels/Insurance/InsuranceDoctorNoteDto.csharp";
import { StreamLineIcon } from "../../../../../SvgIcon/StreamLineIcon";
import { useInsuranceLabels } from "../../../../Utils/Context/labelsContext";

interface NoteFormPropsBase {
  onClose: () => void;
}

interface NoteFormPropsEdit {
  type: "editNote";
  note: InsuranceDoctorNoteDto | null;
  onSubmit: (
    noteId: string,
    noteText: string,
    cb: (status: "error" | "success") => void
  ) => void;
  onDeleteNote: (cb: (status: "error" | "success") => void) => void;
}

interface NoteFormPropsAdd {
  type: "addNote";
  onSubmit: (
    noteText: string,
    cb: (status: "error" | "success") => void
  ) => void;
}

type SpecificProps = NoteFormPropsEdit | NoteFormPropsAdd;

function isAddForm(form: SpecificProps): form is NoteFormPropsAdd {
  return (form as NoteFormPropsAdd).type === "addNote";
}

function isEditForm(form: SpecificProps): form is NoteFormPropsEdit {
  return (form as NoteFormPropsEdit).type === "editNote";
}

export const NoteForm: FC<NoteFormPropsBase & StrictUnion<SpecificProps>> = (
  props
) => {
  const [formSubmissionStatus, setFormSubmissionStatus] =
    useState<InsuranceFormSubmissionStatus>("initial");
  const [noteDeletionStatus, setNoteDeletionStatus] =
    useState<InsuranceFormSubmissionStatus>("initial");
  const { onClose, ...specificProps } = props;
  const {
    buttons,
    headings,
    labels,
    inputs
  } = useInsuranceLabels();

  const cleanupAfterSubmitRequest = (status: "success" | "error") => {
    setFormSubmissionStatus(status);
    if (status === "success") {
      onClose();
    }
  };

  const cleanupAfterDeleteRequest = (status: "success" | "error") => {
    setNoteDeletionStatus(status);
  };

  const onDeleteNote = () => {
    if (
      formSubmissionStatus === "loading" ||
      noteDeletionStatus === "loading" ||
      !isEditForm(specificProps)
    )
      return;
    setNoteDeletionStatus("loading");
    specificProps.onDeleteNote(cleanupAfterDeleteRequest);
  };

  const onSubmit = (noteValue: string) => {
    setFormSubmissionStatus("loading");
    if (isAddForm(specificProps)) {
      const { onSubmit } = specificProps;
      onSubmit(noteValue, cleanupAfterSubmitRequest);
    } else {
      const { note, onSubmit } = specificProps;
      if (note && note.text !== noteValue) {
        onSubmit(note.id, noteValue, cleanupAfterSubmitRequest);
      }
    }
  };

  const footerContent = isEditForm(specificProps) ? (
    <div className="noteForm__footer">
      <button
        type="button"
        className="noteForm__deleteButton"
        onClick={onDeleteNote}
      >
        <StreamLineIcon iconName="s-trash" className="noteForm__trashIcon" />
        <span>
          {buttons.deleteNoteButtonLabel}
          {noteDeletionStatus === "loading" && <LoadingAnimation />}
        </span>
      </button>
      {noteDeletionStatus === "error" && (
        <p className="noteForm__error">
          {labels.removeNoteError}
        </p>
      )}
    </div>
  ) : null;

  const headerText = isEditForm(specificProps)
    ? headings.editNoteHeading
    : headings.createNoteHeading;
  const subHeaderText = isEditForm(specificProps)
    ? headings.editNoteSubHeading
    : headings.createNoteSubHeading;
  const submitButtonLabel = isEditForm(specificProps)
    ? buttons.submitNoteChangesButtonLabel
    : buttons.submitNewNoteButtonLabel;
  const successMessage = isEditForm(specificProps)
    ? labels.editNoteSubmissionSuccess
    : labels.addNoteSubmissionSuccess;
  const errorMessage = isEditForm(specificProps)
    ? labels.editNoteSubmissionError
    : labels.addNoteSubmissionError
  return (
    <SingleFieldModal
      id={isEditForm(specificProps) ? "noteEditForm" : "noteAddForm"}
      header={headerText}
      subHeader={subHeaderText}
      submitButtonLabel={submitButtonLabel}
      submitButtonIcon="s-checkmark"
      closeButtonLabel={buttons.declineNoteButtonLabel}
      inputPlaceholder={inputs.notePlaceholder}
      inputLabel={inputs.noteLabel}
      defaultValue={specificProps.note?.text}
      formSubmissionStatus={formSubmissionStatus}
      successMessage={successMessage}
      errorMessage={errorMessage}
      footerContent={footerContent}
      onClose={onClose}
      onSubmit={onSubmit}
    />
  );
};
